import type { Registratie } from '@/types/domain';
import { ServiceFout, type RegistratieService } from '../types';
import { getHuidigeRol } from '../rolContext';
import { getSupabase, leesbareFout } from './supabaseClient';
import { naarRij, vanRij, type RegistratieRij } from './rowMapping';

const TABEL = 'registraties';

/**
 * De gemeente leest uit een weergave waarin de persoonsgegevens van bewoners
 * niet voorkomen. Dat is geen filter in de app maar een aparte bron in de
 * database, zodat die kolommen ook niet per ongeluk mee kunnen komen.
 */
const GEMEENTE_WEERGAVE = 'registraties_gemeente';

function bron(): string {
  return getHuidigeRol() === 'gemeente' ? GEMEENTE_WEERGAVE : TABEL;
}

/** Supabase geeft standaard maximaal 1000 rijen terug; hier pagineren we erdoorheen. */
const PAGINA = 1000;

/** Postgres-code voor een dubbele waarde in een unieke index. */
const DUBBELE_SLEUTEL = '23505';

export const supabaseRegistratieService: RegistratieService = {
  async haalAlle(): Promise<Registratie[]> {
    const db = getSupabase();
    const alles: Registratie[] = [];

    for (let start = 0; ; start += PAGINA) {
      const { data, error } = await db
        .from(bron())
        .select('*')
        .order('gewijzigd_op', { ascending: false })
        .range(start, start + PAGINA - 1);

      if (error) throw new ServiceFout(leesbareFout(error), error);

      const rijen = (data ?? []) as RegistratieRij[];
      alles.push(...rijen.map(vanRij));

      if (rijen.length < PAGINA) break;
    }

    return alles;
  },

  async opslaan(registratie: Registratie): Promise<Registratie> {
    const db = getSupabase();

    const { data, error } = await db
      .from(TABEL)
      .upsert(naarRij(registratie), { onConflict: 'id' })
      .select()
      .single();

    if (!error && data) return vanRij(data as RegistratieRij);

    /**
     * Twee coaches die dezelfde woning voor het eerst registreren.
     *
     * Beide apps maken dan een eigen registratie aan met hetzelfde BAG-id.
     * De tweede loopt tegen de unieke index aan. In plaats van die fout aan
     * de coach te tonen, schrijven we zijn invulling bij op de rij die er al
     * staat. Het werk van de eerste gaat niet verloren: de trigger zet die
     * versie in de historie voordat hij wordt overschreven.
     */
    const isDubbeleWoning =
      (error as { code?: string } | null)?.code === DUBBELE_SLEUTEL && Boolean(registratie.bagId);

    if (!isDubbeleWoning) {
      throw new ServiceFout(leesbareFout(error), error);
    }

    const { data: bestaande, error: zoekFout } = await db
      .from(TABEL)
      .select('id')
      .eq('bag_id', registratie.bagId)
      .maybeSingle();

    if (zoekFout || !bestaande) {
      throw new ServiceFout(leesbareFout(error), error);
    }

    const bestaandId = (bestaande as { id: string }).id;

    const { data: bijgewerkt, error: bijwerkFout } = await db
      .from(TABEL)
      .update({ ...naarRij(registratie), id: bestaandId })
      .eq('id', bestaandId)
      .select()
      .single();

    if (bijwerkFout || !bijgewerkt) {
      throw new ServiceFout(leesbareFout(bijwerkFout), bijwerkFout);
    }

    return vanRij(bijgewerkt as RegistratieRij);
  },

  async verwijderen(registratie: Registratie): Promise<void> {
    const { error } = await getSupabase().from(TABEL).delete().eq('id', registratie.id);
    if (error) throw new ServiceFout(leesbareFout(error), error);
  },

  abonneerOpWijzigingen(bijWijziging, bijVerwijderen) {
    // De database bepaalt wie wat krijgt: de leesregel op de tabel geldt ook
    // hier, dus een gemeentegebruiker ontvangt via dit kanaal niets.
    const kanaal = getSupabase()
      .channel('registraties-live')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: TABEL },
        (bericht) => {
          if (bericht.eventType === 'DELETE') {
            const oud = bericht.old as { id?: string };
            if (oud?.id) bijVerwijderen(oud.id);
            return;
          }

          const rij = bericht.new as RegistratieRij | null;
          if (rij?.id) bijWijziging(vanRij(rij));
        }
      )
      .subscribe();

    return () => {
      void getSupabase().removeChannel(kanaal);
    };
  },
};
